import GermanDateInput from "@/components/GermanDateInput";
import React, { useEffect, useMemo, useState } from "react";
import { CoCoachPicker } from "@/components/CoCoachPicker";
import { AlertTriangle, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AppointmentDialog({ open, onOpenChange, clients, initialData, defaultClientId, onSaved }) {
  const { user } = useAuth();
  const editing = !!initialData;
  const isOwnUnsignedAppointment = editing && initialData?.coach_user_id === user?.user_id && ["proposed", "accepted"].includes(initialData?.status);
  const canAssignOthers = user?.role === "admin" || user?.role === "manager" || isOwnUnsignedAppointment;
  const isAdmin = (user?.all_roles || [user?.role]).includes("admin") || !!user?.is_master_admin;
  const [staff, setStaff] = useState([]);
  const [allowOver, setAllowOver] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    date: todayIso(),
    time_from: "09:00",
    time_to: "11:00",
    planned_hours: 2,
    planned_ue: 4,
    mode: "online",
    topic: "",
    notes: "",
    section_id: "",
    coach_user_id: "",
    co_coach_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [ueSummary, setUeSummary] = useState(null);
  const [conflict, setConflict] = useState(null); // {has_conflict, busy}
  const [calConnected, setCalConnected] = useState(false);

  useEffect(() => {
    if (open) api.get("/coaches/directory").then((r) => setDirectory(r.data || [])).catch(() => setDirectory([]));
  }, [open]);

  useEffect(() => {
    if (!open || !canAssignOthers) return;
    api.get("/coaches").then((r) => setStaff(r.data || [])).catch(() => setStaff([]));
  }, [open, canAssignOthers]);

  useEffect(() => {
    if (!open) return;
    api.get("/calendar/status")
      .then((r) => setCalConnected(!!r.data?.connected))
      .catch(() => setCalConnected(false));
  }, [open]);

  // Conflict check (debounced) whenever date/time changes and calendar is connected
  useEffect(() => {
    if (!open || !calConnected || !form.date || !form.time_from || !form.time_to) {
      setConflict(null); return;
    }
    if (form.time_to <= form.time_from) { setConflict(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/calendar/check-conflict", {
          date: form.date, time_from: form.time_from, time_to: form.time_to,
        });
        setConflict(data);
      } catch { setConflict(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [open, calConnected, form.date, form.time_from, form.time_to]);

  useEffect(() => {
    if (!open || !form.client_id) { setUeSummary(null); return; }
    let cancelled = false;
    api.get(`/clients/${form.client_id}/ue-summary`)
      .then((r) => { if (!cancelled) setUeSummary(r.data); })
      .catch(() => { if (!cancelled) setUeSummary(null); });
    return () => { cancelled = true; };
  }, [open, form.client_id]);

  const overBudget = useMemo(() => {
    if (!ueSummary) return null;
    const requested = parseFloat(form.planned_ue) || 0;
    // Beim Bearbeiten die eigene, bereits verplante UE (egal welcher Status) aus der Summe
    // herausrechnen, damit sie nicht doppelt gezählt wird — sonst würde ein Termin, der schon
    // Teil des Kontingents ist, beim bloßen Ändern der UE fälschlich als "über Kontingent" gelten.
    const alreadyOwn = initialData ? (parseFloat(initialData.planned_ue) || 0) : 0;
    const wouldUse = (ueSummary.booked_ue - alreadyOwn) + requested;
    const total = ueSummary.total_ue;
    if (total <= 0 || requested <= 0) return null;  // 0-UE-Termine (Nachtrag/Doku) sind immer erlaubt
    if (wouldUse > total) {
      return {
        remaining: Math.max(0, total - (ueSummary.booked_ue - alreadyOwn)),
        overflow: wouldUse - total,
        total,
      };
    }
    return null;
  }, [ueSummary, form.planned_ue, initialData]);

  const clientSections = useMemo(() => {
    const c = clients.find((c) => c.client_id === form.client_id);
    if (!c) return [];
    const rows = [];
    // Onboarding/Offboarding sind immer als Abschnitt wählbar (kein eigenes UE-Ziel nötig) —
    // die UE werden automatisch vom allgemeinen Kunden-UE-Kontingent abgezogen.
    rows.push({ section_id: "onboarding", module_name: null, name: "Onboarding" });
    for (const s of (c.sections || [])) rows.push(s);
    rows.push({ section_id: "offboarding", module_name: null, name: "Offboarding" });
    return rows;
  }, [clients, form.client_id]);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        client_id: initialData.client_id,
        date: initialData.date || todayIso(),
        time_from: initialData.time_from || "",
        time_to: initialData.time_to || "",
        planned_hours: initialData.planned_hours ?? 2,
        planned_ue: initialData.planned_ue ?? 4,
        mode: initialData.mode || "in_person",
        topic: initialData.topic || "",
        notes: initialData.notes || "",
        section_id: initialData.section_id || "",
        coach_user_id: initialData.coach_user_id || "",
        co_coach_ids: initialData.co_coach_ids || [],
      });
    } else {
      setForm((f) => ({
        ...f,
        client_id: defaultClientId || clients[0]?.client_id || "",
        date: todayIso(),
        time_from: "09:00",
        time_to: "11:00",
        planned_hours: 2,
        planned_ue: 4,
        mode: "online",
        topic: "",
        notes: "",
        section_id: "",
        coach_user_id: "",
        co_coach_ids: [],
      }));
    }
  }, [open, initialData, defaultClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kundenliste lädt ggf. nach dem Öffnen — nur fehlenden Kunden ergänzen, Eingaben nie zurücksetzen.
  useEffect(() => {
    if (open && !initialData && !form.client_id && clients[0]?.client_id) setForm((f) => (f.client_id ? f : { ...f, client_id: clients[0].client_id }));
  }, [open, initialData, clients, form.client_id]);

  // Onboarding/Offboarding: automatisch den in der Akte hinterlegten zuständigen Mitarbeiter
  // vorschlagen (bleibt änderbar), damit die UE korrekt auf dessen Tagesziel zählt.
  useEffect(() => {
    if (!open || editing || !canAssignOthers) return;
    const c = clients.find((c) => c.client_id === form.client_id);
    if (!c) return;
    if (form.section_id === "onboarding" && c.onboarding_user_id) {
      setForm((f) => ({ ...f, coach_user_id: c.onboarding_user_id }));
    } else if (form.section_id === "offboarding" && c.offboarding_user_id) {
      setForm((f) => ({ ...f, coach_user_id: c.offboarding_user_id }));
    }
  }, [open, editing, canAssignOthers, form.client_id, form.section_id, clients]);

  const setHours = (v) => {
    const h = parseFloat(v);
    setForm((f) => ({ ...f, planned_hours: v, planned_ue: isNaN(h) ? f.planned_ue : h * 2 }));
  };

  const submit = async () => {
    if (!form.client_id) { toast.error("Bitte einen Kunden auswählen"); return; }
    if (!form.date) { toast.error("Datum fehlt"); return; }
    if (form.time_from && form.time_to && form.time_to <= form.time_from) {
      toast.error("Endzeit muss nach Startzeit liegen"); return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        date: form.date,
        time_from: form.time_from || null,
        time_to: form.time_to || null,
        planned_hours: parseFloat(form.planned_hours) || 0,
        planned_ue: parseFloat(form.planned_ue) || 0,
        mode: form.mode,
        topic: form.topic || null,
        notes: form.notes || null,
        section_id: form.section_id || null,
        co_coach_ids: form.co_coach_ids || [],
        allow_overbooking: allowOver,
      };
      if (canAssignOthers && form.coach_user_id) {
        payload.coach_user_id = form.coach_user_id;
      }
      if (editing) {
        await api.put(`/appointments/${initialData.appointment_id}`, payload);
        toast.success("Termin aktualisiert");
      } else {
        const { data } = await api.post("/appointments", payload);
        toast.success("Termin vorgeschlagen — der Kunde bekommt eine Benachrichtigung");
        if (data?.google_sync_warning) toast.warning(data.google_sync_warning, { duration: 9000 });
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="appointment-dialog">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
            {editing ? "Termin bearbeiten" : "Neuer Termin"}
          </div>
          <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
            Coaching-Termin
          </DialogTitle>
          <DialogDescription className="sr-only">Schlage dem Kunden einen Termin vor.</DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 space-y-5 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Kunde *</label>
            <select
              data-testid="appt-client-select"
              className="input-tech"
              value={form.client_id}
              disabled={editing}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, client_id: v })); }}
            >
              <option value="">— Kunde auswählen —</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.name} {c.customer_number ? `· Kd-Nr ${c.customer_number}` : ""}
                </option>
              ))}
            </select>
            {clients.length === 0 && (
              <p className="text-xs text-amber-700 mt-2">
                Keine Kunden verfügbar. Bitte den Admin bitten, einen Kunden anzulegen.
              </p>
            )}
          </div>

          {clientSections.length > 0 && (
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                Abschnitt (für UE-Fortschritt)
              </label>
              <select
                data-testid="appt-section-select"
                className="input-tech"
                value={form.section_id}
                onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, section_id: v })); }}
              >
                <option value="">— kein Abschnitt zugeordnet —</option>
                {clientSections.map((s) => (
                  <option key={s.section_id} value={s.section_id}>
                    {s.module_name ? `${s.module_name} · ` : ""}{s.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">
                Ohne Abschnitt zählt die UE nicht im Fortschrittsbalken des Abschnitts.
              </p>
            </div>
          )}

          {canAssignOthers && (
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">
                {isOwnUnsignedAppointment && !(user?.role === "admin" || user?.role === "manager")
                  ? "An wen übergeben? (Vertretung)"
                  : "Für welchen Mitarbeiter?"}
              </label>
              <select
                data-testid="appt-coach-select"
                className="input-tech"
                value={form.coach_user_id}
                onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, coach_user_id: v })); }}
              >
                <option value="">— ich selbst —</option>
                {staff.map((s) => (
                  <option key={s.user_id} value={s.user_id}>{s.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">
                {isOwnUnsignedAppointment && !(user?.role === "admin" || user?.role === "manager")
                  ? "Du kannst diesen von dir vorgeschlagenen Termin bei Vertretung an eine andere Person übergeben."
                  : "Die UE zählt auf das Tagesziel dieses Mitarbeiters (z.B. bei Onboarding/Offboarding)."}
              </p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Datum *</label>
              <GermanDateInput data-testid="appt-date"  className="input-tech font-mono" value={form.date} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, date: v })); }} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Von</label>
              <input data-testid="appt-time-from" type="time" className="input-tech font-mono" value={form.time_from} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, time_from: v })); }} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Bis</label>
              <input data-testid="appt-time-to" type="time" className="input-tech font-mono" value={form.time_to} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, time_to: v })); }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Stunden</label>
              <input data-testid="appt-hours" type="number" step="0.25" min="0" className="input-tech font-mono" value={form.planned_hours} onChange={(e) => setHours(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">UE (= 2×h)</label>
              <input data-testid="appt-ue" type="number" step="0.5" min="0" className="input-tech font-mono" value={form.planned_ue} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, planned_ue: v })); }} />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Modus</label>
            <div className="grid grid-cols-2 gap-3" data-testid="appt-mode-group">
              {[
                { v: "in_person", l: "Präsenz" },
                { v: "online", l: "Remote / Online" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`appt-mode-${opt.v}`}
                  onClick={() => setForm({ ...form, mode: opt.v })}
                  className={`px-4 py-3 text-sm border transition-colors ${
                    form.mode === opt.v
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Thema</label>
            <input data-testid="appt-topic" className="input-tech" placeholder="z.B. Bewerbungsstrategie" value={form.topic} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, topic: v })); }} />
          </div>
          <CoCoachPicker staff={directory} mainCoachId={form.coach_user_id || initialData?.coach_user_id || user?.user_id}
            value={form.co_coach_ids || []} onChange={(v) => setForm((f) => ({ ...f, co_coach_ids: v }))} />
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2 block">Notizen</label>
            <input data-testid="appt-notes" className="input-tech" value={form.notes} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, notes: v })); }} />
          </div>

          {ueSummary && (
            <div className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 px-3 py-2" data-testid="ue-info">
              <span className="text-slate-600 font-mono">
                UE-Balance: <b>{ueSummary.unbooked_ue}</b> von {ueSummary.total_ue} noch frei
                <span className="text-slate-400"> · {ueSummary.booked_ue} bereits verplant (inkl. offener Termine)</span>
              </span>
              {overBudget && (
                <span className="text-red-700 font-medium flex items-center gap-1">
                  <AlertTriangle size={12} strokeWidth={2} /> +{overBudget.overflow.toFixed(1)} UE über Kontingent
                </span>
              )}
            </div>
          )}

          {overBudget && (
            <div className="flex items-start gap-3 border border-red-300 bg-red-50 text-red-900 px-4 py-3" data-testid="ue-warning">
              <AlertTriangle size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed">
                <b>UE-Kontingent überschritten.</b><br />
                Für diesen Termin stehen nur noch <b>{overBudget.remaining} UE</b> zur Verfügung (bereits
                verplant inkl. anderer offener Termine). Mit <b>{form.planned_ue} UE</b> wären das
                {" "}<b>{overBudget.overflow.toFixed(1)} UE</b> mehr, als der Kunde insgesamt bewilligt hat.
                Reduziere die UE{isAdmin ? " – oder trage den Termin als Admin bewusst als Nachtrag über dem Kontingent ein:" : " oder bitte einen Admin um einen Nachtrag über dem Kontingent."}
                {isAdmin && (
                  <label className="flex items-center gap-2 mt-2 font-medium">
                    <input type="checkbox" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} data-testid="appt-allow-overbooking" />
                    Trotzdem eintragen ({overBudget.overflow.toFixed(1)} UE über Kontingent)
                  </label>
                )}
              </div>
            </div>
          )}

          {!overBudget && isAdmin && (
            <label className="flex items-start gap-2 text-xs text-slate-600 border border-slate-200 px-4 py-3" data-testid="appt-overbooking-row">
              <input type="checkbox" className="mt-0.5" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} data-testid="appt-allow-overbooking" />
              <span>
                <b className="text-slate-800">Nachtrag (nur Admin):</b> Grenzen bewusst überschreiten – erlaubt diesen Termin auch dann,
                wenn das UE-Kontingent ausgeschöpft ist oder der Bewilligungszeitraum bereits endet (z. B. vergessener Termin).
              </span>
            </label>
          )}

          {calConnected && conflict?.has_conflict && (
            <div className="flex items-start gap-3 border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3" data-testid="calendar-conflict-warning">
              <CalendarClock size={16} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed">
                <b>Achtung: Kalender-Konflikt.</b> In deinem Google-Kalender ist der Zeitraum bereits belegt
                ({conflict.busy?.length || 0} Eintrag{(conflict.busy?.length || 0) === 1 ? "" : "e"}). Du kannst
                den Termin trotzdem speichern.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-8 py-5 border-t border-slate-100 flex gap-3">
          <button data-testid="appt-cancel" onClick={() => onOpenChange(false)} className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
            Abbrechen
          </button>
          <button data-testid="appt-save" onClick={submit} disabled={saving || (!!overBudget && !allowOver)} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            {saving ? "Speichert…" : overBudget ? (allowOver ? "Trotzdem speichern (Nachtrag)" : "UE reduzieren oder Nachtrag bestätigen") : editing ? "Aktualisieren" : "Termin vorschlagen"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
